import React, { useState } from "react";
import { Bell, Check, Clock, FileText, AlertTriangle, Info, Lightbulb, Mail, MoreHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Sample notification data
const notifications = [
  { id: 1, type: "info", title: "Welcome to OG01!", message: "Thank you for joining. Start exploring your new marketing tools.", date: "2023-11-25T10:30:00", read: true, icon: Info },
  { id: 2, type: "alert", title: "Subscription Expiring Soon", message: "Your premium subscription will expire in 7 days. Renew now to avoid service interruption.", date: "2023-12-01T14:45:00", read: false, icon: AlertTriangle },
  { id: 3, type: "success", title: "Campaign Results", message: "Your recent campaign 'Summer Promotion' has achieved 125% of the target goal.", date: "2023-12-02T09:15:00", read: false, icon: Check },
  { id: 4, type: "reminder", title: "Team Meeting", message: "Reminder: Marketing strategy meeting tomorrow at 2:00 PM.", date: "2023-12-03T16:30:00", read: false, icon: Clock },
  { id: 5, type: "document", title: "Monthly Report", message: "Your November analytics report is now available for download.", date: "2023-12-04T11:20:00", read: true, icon: FileText },
  { id: 6, type: "tip", title: "Optimization Tip", message: "Try our new SEO analyzer tool to improve your website ranking.", date: "2023-12-05T08:45:00", read: true, icon: Lightbulb },
  { id: 7, type: "message", title: "New Message", message: "You have received a new message from support regarding your recent inquiry.", date: "2023-12-06T13:10:00", read: false, icon: Mail },
];

const TYPE_COLOR: Record<string, string> = {
  info: "#8B5CF6", alert: "#F59E0B", success: "#22C55E", reminder: "#3B82F6",
  document: "#8B5CF6", tip: "#6366F1", message: "#0EA5E9",
};

export function AccountNotifications() {
  const [userNotifications, setUserNotifications] = useState(notifications);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric', hour12: true }).format(date);
  };

  const markAsRead = (id: number) => setUserNotifications(userNotifications.map((n) => n.id === id ? { ...n, read: true } : n));
  const markAllAsRead = () => setUserNotifications(userNotifications.map((n) => ({ ...n, read: true })));
  const unreadCount = userNotifications.filter((n) => !n.read).length;
  const deleteNotification = (id: number) => setUserNotifications(userNotifications.filter((n) => n.id !== id));

  return (
    <div className="overflow-hidden rounded-2xl border border-white/[0.07] bg-og01-card p-6">
      <div className="mb-5 flex items-start justify-between gap-4">
        <div>
          <div className="font-heading text-[20px] text-og01-text">Notifications</div>
          <div className="mt-1 text-[13px] text-og01-text-secondary">Manage your notifications and preferences</div>
        </div>
        <div className="flex items-center gap-2.5">
          {unreadCount > 0 && (
            <button onClick={markAllAsRead}
              className="rounded-[10px] border border-white/[0.07] bg-og01-card px-3 py-2 text-[12.5px] font-semibold text-og01-text transition-colors hover:border-white/[0.12]">
              Mark all as read
            </button>
          )}
          <button className="grid h-9 w-9 place-items-center rounded-[10px] border border-white/[0.07] bg-og01-card text-og01-text-secondary transition-colors hover:border-white/[0.12] hover:text-og01-text">
            <Bell className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-[13.5px]">
          <thead>
            <tr>
              <th className="px-4 pb-3 text-left text-[11px] font-medium uppercase tracking-[0.05em] text-og01-text-muted">Message</th>
              <th className="px-4 pb-3 text-left text-[11px] font-medium uppercase tracking-[0.05em] text-og01-text-muted">Date</th>
              <th className="px-4 pb-3 text-left text-[11px] font-medium uppercase tracking-[0.05em] text-og01-text-muted">Status</th>
              <th className="px-4 pb-3 text-right text-[11px] font-medium uppercase tracking-[0.05em] text-og01-text-muted">Actions</th>
            </tr>
          </thead>
          <tbody>
            {userNotifications.length === 0 ? (
              <tr><td colSpan={4} className="border-t border-white/[0.07] py-16 text-center text-og01-text-muted">No notifications</td></tr>
            ) : userNotifications.map((n) => {
              const Icon = n.icon;
              const color = TYPE_COLOR[n.type] || "#8B5CF6";
              return (
                <tr key={n.id} className="hover:bg-white/[0.018]">
                  <td className="border-t border-white/[0.07] px-4 py-4">
                    <div className="flex items-start gap-3">
                      <div className="grid h-8 w-8 flex-none place-items-center rounded-lg" style={{ background: `color-mix(in srgb, ${color} 16%, transparent)` }}>
                        <Icon className="h-[18px] w-[18px]" style={{ color }} />
                      </div>
                      <div>
                        <div className="font-semibold text-og01-text">{n.title}</div>
                        <div className="text-[12.5px] text-og01-text-secondary">{n.message}</div>
                      </div>
                    </div>
                  </td>
                  <td className="whitespace-nowrap border-t border-white/[0.07] px-4 py-4 text-og01-text-muted">{formatDate(n.date)}</td>
                  <td className="border-t border-white/[0.07] px-4 py-4">
                    <span className="inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold" style={n.read ? { color: "#8080A0", background: "rgba(255,255,255,.05)" } : { color: "#F59E0B", background: "color-mix(in srgb, #F59E0B 14%, transparent)" }}>
                      {n.read ? "Read" : "Unread"}
                    </span>
                  </td>
                  <td className="border-t border-white/[0.07] px-4 py-4 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-og01-text-secondary hover:bg-white/10 hover:text-og01-text">
                          <span className="sr-only">Open menu</span>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuLabel>Actions</DropdownMenuLabel>
                        {!n.read && <DropdownMenuItem onClick={() => markAsRead(n.id)}>Mark as read</DropdownMenuItem>}
                        <DropdownMenuItem onClick={() => alert(`View details for notification: ${n.id}`)}>View details</DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => deleteNotification(n.id)} className="text-destructive focus:bg-destructive focus:text-destructive-foreground">Delete</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
